import { t } from '@lingui/macro'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { atom, useRecoilState } from 'recoil'
import { useTheme } from 'styled-components'

import { Padding } from '@/@bothrs-ui/web/components/layout'
import { PaginatedView } from '@/@bothrs-ui/web/pages/paginated-view'
import { localStorageEffect } from '@/capacitor/recoil/localstorage-effect'
import { useTenantHref } from '@/modules/shared/hooks/useTenantHref'
import { UspSlide } from './components/UspSlide'
import { StButton, StButtonContainer, StContainer, StScreenView } from './UspSlides.page.styled'

import type { Props as SlideProps } from './components/UspSlide'

export const localUspSlidesAtom = atom<boolean>({
  key: '@app/local-Usp-slides',
  default: false,
  effects: [localStorageEffect('@app/local-Usp-slides')],
})

const slides: SlideProps[] = [
  {
    title: t({
      message: 'Boeken zonder zorgen',
      comment: 'Usp booking title',
    }),
    message: t({
      message: 'Maak en beheer je afspraken in enkele klikken via MijnZiekenhuis.',
      comment: 'Usp booking body',
    }),
    graphic: '/img/svg/usp-booking.svg',
  },
  {
    title: t({
      message: 'Agenda',
      comment: 'Usp page 2 title',
    }),
    message: t({
      message: 'Je agenda geeft je een overzicht van je afspraken en opnames.',
      comment: 'Usp page 2 body',
    }),
    graphic: '/img/svg/usp-task.svg',
  },
  {
    title: t({
      message: 'Taken',
      comment: 'Usp page 3 title',
    }),
    message: t({
      message: "Openstaande en afgewerkte taken kan je terugvinden onder 'Taken'.",
      comment: 'Usp page 3 body',
    }),
    graphic: '/img/svg/usp-contact.svg',
  },
  {
    title: t({
      message: 'Altijd en overal in contact',
      comment: 'Usp page 4 title',
    }),
    message: t({
      message:
        'De app zorgt voor een vlotte uitwisseling van informatie met ons ziekenhuis. We begeleiden je doorheen je hele traject.',
      comment: 'Usp page 4 body',
    }),
    graphic: '/img/svg/usp-4.svg',
  },
  // TODO: Uncomment when we have a new usp
  // {
  //   title: t({
  //     message: 'Eén app voor je hele familie',
  //     comment: 'Usp page 4 title',
  //   }),
  //   message: t({
  //     message:
  //       'Beheer via de profielknop ( {value} ) afspraken en meer voor jezelf of familieleden.',
  //     comment: 'Usp page 4 body',
  //     values: {
  //       value: `<fa-circle-user />`, // Placeholder for JSX Icon (always start with fa-[icon-name] - default is circle-user)
  //     },
  //   }),
  //   graphic: '/img/svg/usp-4.svg',
  // },
  {
    title: t({
      message: 'Bouw aan je welzijn',
      comment: 'Usp care title',
    }),
    message: t({
      message:
        'Profiteer van medisch onderbouwde informatie, activiteiten en trajecten om je gezondheid te verbeteren.',
      comment: 'Usp care body',
    }),
    graphic: '/img/svg/usp-care.svg',
  },
]
const UspSlidesPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const tenantHref = useTenantHref()
  const homePath = tenantHref('/')
  const [currentSlideInfo, setCurrentSlideInfo] = useState<[number, number]>([0, 1])
  const [, setLocalUspSlidesAtom] = useRecoilState(localUspSlidesAtom)

  // Interaction methods
  // ------------------------------------------------------------------------- /
  const { finishUspSlides, handleContinue } = useMemo(() => {
    function finishUspSlides() {
      setLocalUspSlidesAtom(true)
      navigate(homePath)
    }
    function handleContinue() {
      setCurrentSlideInfo(([currentSlide, direction]) => [currentSlide + 1, direction])
    }
    return { finishUspSlides, handleContinue }
  }, [navigate, setLocalUspSlidesAtom])

  return (
    <StScreenView>
      <StContainer>
        <PaginatedView currentSlideInfo={currentSlideInfo} setCurrentSlideInfo={setCurrentSlideInfo}>
          {slides.map((slide, index) => {
            return <UspSlide key={index} {...slide} />
          })}
        </PaginatedView>
        <StButtonContainer>
          {currentSlideInfo[0] < slides?.length - 1 ? (
            <>
              <StButton variant='Primary' type='submit' onClick={handleContinue}>
                {t({
                  id: 'usp.continue',
                  comment: 'next page',
                  message: 'Volgende',
                })}
              </StButton>
              <Padding py={theme.UI.Spacing.Space2} />
              <StButton variant='Secondary' type='button' onClick={finishUspSlides}>
                {t({
                  id: 'usp.skip',
                  comment: 'skip usp',
                  message: 'Overslaan',
                })}
              </StButton>
            </>
          ) : (
            <StButton variant='Primary' type='submit' onClick={finishUspSlides}>
              {t({
                id: 'usp.done',
                comment: 'End usp',
                message: 'Ga verder',
              })}
            </StButton>
          )}
        </StButtonContainer>
      </StContainer>
    </StScreenView>
  )
}

export { UspSlidesPage }
